import * as React from 'react';

import { TextField, BoxProps } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FieldValues, UseControllerProps } from 'react-hook-form';

import { DateTime } from 'luxon';

import _startCase from 'lodash/startCase';

import { useFormUpdateProxy } from '../core/context/FormUpdateProxyContext';
import useEditableController from '../core/api/hooks/common/useEditableController';

import ReadOnlyField from './ReadOnlyField';
import EditableField from './EditableField';

interface IControlledDatePicker<TFieldValues extends FieldValues> extends UseControllerProps<TFieldValues> {
  label?: string,
  readOnly?: boolean,
  disableEditPopper?: boolean,
  isEditable?: boolean,
}

function ControlledDatePicker<TFieldValues extends FieldValues>({
  name,
  label,
  readOnly,
  disableEditPopper,
  control,
  rules,
  isEditable = true,
}: IControlledDatePicker<TFieldValues>): JSX.Element {
  const [dateValue, setDateValue] = React.useState<DateTime | null>(null);

  const { requestUpdate } = useFormUpdateProxy();
  const {
    field:
    {
      value: fieldValue,
      onBlur: fieldOnBlur,
      onChange: fieldOnChange,
    },
    fieldState,
    revertChanges,
    commitChanges,
  } = useEditableController<TFieldValues>({
    name,
    control,
  });
  React.useEffect(() => {
    if (DateTime.isDateTime(fieldValue)) {
      setDateValue(fieldValue);
    } else {
      setDateValue(DateTime.fromISO(fieldValue as string));
    }
  }, [fieldValue]);

  return (
    <EditableField
      fieldName={name}
      renderMode={readOnly ? 'read' : 'edit'}
      disableEditPopper={disableEditPopper}
      saveSuccess={() => {
        commitChanges();
        requestUpdate();
      }}
      onEditCancel={revertChanges}
      renderEditField={() => (
        <DatePicker
          value={dateValue}
          onChange={(changeValue: DateTime | null) => fieldOnChange(changeValue?.toISODate())}
          slotProps={{
            textField: {
              fullWidth: true,
              label: `${label || _startCase(name)}`,
              required: rules?.required === true ?? false,
              variant: 'outlined',
              error: fieldState.invalid,
              onBlur: fieldOnBlur,
              helperText: fieldState.invalid && fieldState.error ? fieldState.error.message : null,
            },
          }}
          disabled={!isEditable}
        />
      )}
      renderReadField={() => (
        <ReadOnlyField
          name={label || _startCase(name)}
          value={DateTime.fromISO(fieldValue).toLocaleString()}
        />
      )}
    />
  );
}

export default ControlledDatePicker;
